import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type {
  FilterResponse,
  SearchResponse,
} from '@jane/business-admin/types';
import { tFilterResponse, tSearchResponse } from '@jane/business-admin/types';
import { fetchWithDecode, isTransientError } from '@jane/business-admin/util';
import { janeApiV2 } from '@jane/shared/data-access';

import { STORES_URL } from './stores';

const fetchProductSearchFilters = async (
  storeId: string,
  category?: string[],
  subcategory?: string[]
): Promise<FilterResponse> => {
  const urlParams = new URLSearchParams();
  category &&
    category.length > 0 &&
    urlParams.append('category', category.join(','));
  subcategory &&
    subcategory.length > 0 &&
    urlParams.append('subcategory', subcategory.join(','));

  const url = `${STORES_URL}/${storeId}/product_filters?${urlParams.toString()}`;
  const data = await fetchWithDecode(
    janeApiV2.get<FilterResponse>(url),
    tFilterResponse,
    url
  );

  return data;
};

export const useProductSearchFilters = (
  storeId: string,
  category?: string[],
  subcategory?: string[],
  enabled?: boolean
) => {
  return useQuery<FilterResponse>({
    queryFn: () => fetchProductSearchFilters(storeId, category, subcategory),
    queryKey: ['searchFilters', storeId, category, subcategory],
    staleTime: Infinity,
    useErrorBoundary: (error) => !isTransientError(error),
    enabled,
  });
};

const fetchGlobalProductSearchFilters = async (
  kind?: string[],
  retail_org_id?: number
): Promise<FilterResponse> => {
  const urlParams = new URLSearchParams();
  kind && kind.length > 0 && urlParams.append('filters[kind]', kind.join(','));
  retail_org_id &&
    urlParams.append('organization_id', retail_org_id.toString());

  const url = `/business/specials/products/search_filters?${urlParams.toString()}`;
  const data = await fetchWithDecode(
    janeApiV2.get<FilterResponse>(url),
    tFilterResponse,
    url
  );

  return data;
};

export const useGlobalProductSearchFilters = (
  kind?: string[],
  retail_org_id?: number,
  enabled?: boolean
) => {
  return useQuery<FilterResponse>({
    queryFn: () => fetchGlobalProductSearchFilters(kind, retail_org_id),
    queryKey: ['globalSearchFilters', kind, retail_org_id],
    staleTime: Infinity,
    useErrorBoundary: true,
    enabled,
  });
};

type GlobalProductSearchParams = {
  brand?: string;
  enabled?: boolean;
  in_stock?: boolean;
  includeHidden?: boolean;
  kind?: string;
  lineage?: string;
  name?: string;
  page?: number;
  product_ids?: string[];
  retail_org_id?: number;
  rowId?: string;
  subtype?: string;
  weight?: string;
};

const fetchGlobalProductSearch = async (
  params: GlobalProductSearchParams
): Promise<SearchResponse> => {
  const urlParams = new URLSearchParams();
  params.rowId && urlParams.append('row_id', params.rowId);
  params.brand && urlParams.append('brand', params.brand);
  params.kind && urlParams.append('kind', params.kind);
  params.lineage && urlParams.append('lineage', params.lineage);
  params.name && urlParams.append('name', params.name);
  params.subtype && urlParams.append('subtype', params.subtype);
  params.weight && urlParams.append('weight', params.weight);
  params.includeHidden &&
    urlParams.append('include_hidden', params.includeHidden.toString());
  params.in_stock !== undefined &&
    urlParams.append('in_stock', params.in_stock.toString());
  params.page && urlParams.append('page', params.page.toString());
  params.product_ids?.forEach((id) => urlParams.append('product_ids[]', id));
  params.retail_org_id &&
    urlParams.append('organization_id', params.retail_org_id.toString());

  const url = `/business/specials/products/search?${urlParams.toString()}`;
  const data = await fetchWithDecode(
    janeApiV2.get<SearchResponse>(url),
    tSearchResponse,
    url
  );

  return data;
};

export const useGlobalProductSearch = (params: GlobalProductSearchParams) => {
  return useInfiniteQuery<SearchResponse>({
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchGlobalProductSearch({
        ...params,
        page: pageParam,
      });
      return {
        ...data,
        pageParam,
      };
    },
    queryKey: ['searchGlobalProducts', params],
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage?.meta.page < lastPage.meta.number_of_pages;
      return hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    staleTime: Infinity,
    useErrorBoundary: true,
    enabled: params?.enabled,
  });
};
